import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  IElementGenericImgSheet,
  IElementImg2d,
  IElementRoomSection,
  IElementSection,
  IElementType,
  IElementTypeHint,
  isIElementGenericImgSheet,
  isIElementImg360,
  isIElementPanoInOdometryPath,
  isIElementRoomSection,
  isIElementWithTypeAndHint,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  isSingleScanSection,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { selectImgsFromRooms } from "../mode-selectors";
import { CurrentAreaData } from "../select-current-area-data";

export type TagsManagementScene = {
  /** The list of scans for each dataset/data session */
  scans: Record<GUID, IElementSection[]>;
  /** The list of rooms in the area */
  rooms: IElementSection[];
  /** The list of sheet in the area */
  sheets: IElementGenericImgSheet[];

  /** The list of location markers in the area */
  locationMarkers: IElementRoomSection[];
  /** The list of attached photos from photo only locations */
  photos: IElementImg2d[];
  /** The corresponding room sections for photos */
  photoSections: IElementRoomSection[];
};

/**
 * A memoized selector that returns the data for the tags management scene. It is
 * re-evaluated only when the iElements slice changes
 *
 * @returns The sheets and panos that should be visible in the current area
 * @param state The state of the store
 * @param areaData The data of the current area
 */
export const selectTagsManagementScene = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, areaData: CurrentAreaData | undefined) => areaData,
    ],
    (iElements: IElementsState, areaData: CurrentAreaData | undefined) => {
      const iElementsStore = { iElements };
      const scene: TagsManagementScene = {
        scans: {},
        rooms: [],
        sheets: [],
        locationMarkers: [],
        photos: [],
        photoSections: [],
      };
      if (!areaData?.area) return scene;

      for (const dataSession of [
        ...areaData.dataSessions2d,
        ...areaData.dataSessions5d,
      ]) {
        const scans = selectChildrenDepthFirst(
          dataSession,
          isIElementScan,
        )(iElementsStore).filter((scan) => {
          const pano = selectChildDepthFirst(
            scan,
            isIElementImg360,
            1,
          )(iElementsStore);
          return !!pano && !isIElementPanoInOdometryPath(pano);
        });

        if (scans.length > 0) {
          scene.scans[dataSession.id] = scans;
        }
      }

      const rooms = areaData.roomsTimeSeries.map((timeSeries) =>
        selectAncestor(timeSeries, isIElementRoomSection)(iElementsStore),
      );

      scene.rooms = rooms
        .filter((r) =>
          selectChildDepthFirst(r, isIElementImg360)(iElementsStore),
        )
        .filter(isValid);
      scene.sheets = selectChildrenDepthFirst(
        areaData.area,
        isIElementGenericImgSheet,
      )(iElementsStore);

      scene.locationMarkers = areaData.locationMarkers;

      scene.photos = selectImgsFromRooms(areaData.roomsWithImg2d)(
        iElementsStore,
      );
      scene.photoSections = scene.photos.map((photo) => {
        const roomSection = selectAncestor(
          photo,
          isIElementRoomSection,
        )(iElementsStore);
        assert(roomSection, "2D Photo location must exist.");
        return roomSection;
      });

      return scene;
    },
  ),
);

/**
 * @returns true if the input iElement is a valid scan type
 * @param iElement The element to check
 */
function isIElementScan(iElement: IElement): iElement is IElementSection {
  return (
    isIElementWithTypeAndHint(
      iElement,
      IElementType.section,
      IElementTypeHint.structuredE57,
    ) || isSingleScanSection(iElement)
  );
}
